import { useContext } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { BsPlusCircle } from 'react-icons/bs';
import { BiDetail } from 'react-icons/bi';
import { AiOutlineDownload } from 'react-icons/ai';
import { Button, DateInput, Input } from 'ypf-pas-general-ui-components';

import {
  downloadWorkReport,
  postWorkReport,
  putNewWorkReportDates
} from '../../../services/workReport';
import { getPositions } from '../../../services/adminContracts';
import LoaderContext from '../../../context/LoaderProvider';
import useToast from '../../../hooks/useToast';
import { formatAmountDate, formatFullDate, formatsEnum } from '../../../utils/formatUtils';
import useWorkReport from '../../../hooks/useWorkReport';
import { useWorkReportModals } from '../../../context/WorkReportModalsContext';
import { useFilterContext } from '../../../context/FilterDetailSDSContext';

const WorkReportHeader = (props) => {
  const {
    contractNumber,
    disabledByRol,
    hasWorkReport,
    endingDate,
    setEndingDate,
    editDate,
    setEditDate,
    backupDates,
    setBackupDates,
    numberDays,
    applyTemplate,
    setNumberDays,
    createNewWorkReport,
    setShowModalAddLine,
    setPositions,
    setCreateNewWorkReport,
    setLoading,
    setShowModalOutOfRange,
    hasLines,
    setHasLines,
    isASDS,
    initialDate,
    setInitialDate,
    uploadTemplate,
    disabledEditStateCancelOrFinish

    // workPartPermissions
  } = props;

  const { id } = useParams();

  const { setLoader } = useContext(LoaderContext);

  const { setNewToast } = useToast();
  const actualDate = formatFullDate(new Date(), formatsEnum.FORMAT_DATE_2);
  const fetchDataWithDefaultParams = () => {
    uploadTemplate();
  };
  const { activeTemplateWorkReportModal } = useWorkReportModals();

  const { filter } = useFilterContext();
  const {
    fetchLines,
    setWorkReportId,
    workReportId,
    searchValue,
    setSearchValue,
    calendarDate,
    setCalendarDate,
    setEndDate
  } = useWorkReport();

  const fetchPositions = async () => {
    try {
      const response = await getPositions(contractNumber, false);
      const positionAdapter = response?.data.map((position) => ({
        id: position.id,
        nombre: `${position.numeroPosicion} - ${position.descripcionSap}`,
        descripcionSap: position.descripcionSap,
        descripcionYClick: position.descripcionYClick,
        numeroPosicion: position.numeroPosicion
      }));

      setPositions(positionAdapter);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  const createWorkReport = async () => {
    setLoader(true);
    try {
      const response = await postWorkReport({
        idSolicitudServicio: Number(id),
        fechaInicio: initialDate,
        cantDias: Number(numberDays)
      });

      setHasLines(false);
      setWorkReportId(response.id);
      setCreateNewWorkReport(true);
      setLoader(false);
    } catch (error) {
      setLoader(false);
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  const download = async () => {
    setLoader(true);
    setNewToast({
      type: 'INFO',
      message: 'Descargando, por favor espere.'
    });
    try {
      await downloadWorkReport(workReportId);

      setLoader(false);
    } catch (error) {
      setLoader(false);
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  const updateReportDates = async () => {
    setLoader(true);
    try {
      const res = await putNewWorkReportDates(workReportId, initialDate, numberDays);

      setEndDate(endingDate);
      setLoader(false);
      if (res.actualizado) {
        setCalendarDate(initialDate);
        setNewToast({
          type: 'SUCCESS',
          message: res.titulo
        });
        await fetchLines(initialDate, undefined, filter);
      } else {
        setShowModalOutOfRange(true);
      }
    } catch (error) {
      setLoader(false);
      setNewToast({
        type: 'ERROR',
        message: error.message
      });
    }
  };

  const handleChangeSearch = (e) => {
    setSearchValue(e.target.value);
  };

  const onSearch = async () => {
    await fetchLines(calendarDate, true, filter);
  };

  return (
    <>
      <Row className="mt-3 justify-content-start">
        <Col sm={3}>
          <DateInput
            disabled={((createNewWorkReport || hasWorkReport) && !editDate) || disabledByRol}
            label="Fecha de inicio "
            max={isASDS ? actualDate : null}
            name="initialDate"
            onChange={(e) => {
              setInitialDate(e.target.value);
            }}
            placeholder="Seleccioná una fecha"
            value={initialDate}
            widthDateInput="100%"
          />
        </Col>
        <Col sm={3}>
          <Input
            disabled={(!initialDate || createNewWorkReport || hasWorkReport) && !editDate}
            label="Cantidad de días"
            name="numberDays"
            onChange={(e) => {
              setNumberDays(e.target.value);
            }}
            placeholder="Seleccioná una cantidad"
            type="number"
            value={numberDays || '-'}
            widthDateInput="100%"
          />
        </Col>
        <div
          style={{
            width: '155px'
          }}
        >
          <Input
            border="transparent"
            disabled
            errors=""
            label="Fecha de fin"
            name="endDate"
            onChange={() => {
              /* empty */
            }}
            placeholder="DD/MM/AAAA"
            type="string"
            value={formatAmountDate(endingDate) || ''}
            widthDateInput="100%"
          />
        </div>
        {(createNewWorkReport || hasWorkReport) && !editDate && (
          <Col className="mt-4" sm={2}>
            <Button
              disabled={disabledByRol || disabledEditStateCancelOrFinish}
              label="Editar"
              onClick={() => {
                setEditDate(true);
                setBackupDates({
                  initialDate,
                  numberDays
                });
              }}
              type="button"
              variant="outline-primary"
              widthButton="82px"
            />
          </Col>
        )}
        {editDate && (
          <Col className="d-flex justify-content-between" sm={2}>
            <Col className="mt-4" sm={2}>
              <Button
                label="Cancelar"
                onClick={() => {
                  setEditDate(false);
                  setInitialDate(backupDates.initialDate);
                  setNumberDays(backupDates.numberDays);
                }}
                type="button"
                variant="outline-primary"
                widthButton="120px"
              />
            </Col>
            <Col className="mt-4" sm={2}>
              <Button
                disabled={
                  !initialDate ||
                  !endingDate ||
                  !numberDays ||
                  !(
                    initialDate !== backupDates.initialDate ||
                    Number(numberDays) !== Number(backupDates.numberDays)
                  )
                }
                label="Guardar"
                onClick={() => {
                  setEditDate(false);
                  updateReportDates();
                }}
                type="button"
                variant="primary"
                widthButton="120px"
              />
            </Col>
          </Col>
        )}
        <Col sm={12}>
          <div className="d-flex justify-content-end my-2">
            {!createNewWorkReport && !hasWorkReport ? (
              <div
                className="d-flex justify-content-between"
                style={{
                  width: '220px'
                }}
              >
                <Button
                  disabled={disabledByRol}
                  label="Cancelar"
                  onClick={() => {
                    setEndingDate(null);
                    setNumberDays('');
                    setInitialDate(null);
                  }}
                  type="button"
                  variant="outline-primary"
                  widthButton="100px"
                />
                <Button
                  disabled={!endingDate}
                  label="Continuar"
                  onClick={() => {
                    createWorkReport();
                  }}
                  type="button"
                  variant="primary"
                  widthButton="100px"
                />
              </div>
            ) : (
              <Col sm={12}>
                <Row>
                  <Col sm={5}>
                    {hasLines && (
                      <Input
                        name="searchLine"
                        onChange={handleChangeSearch}
                        onSearchFunction={() => onSearch()}
                        placeholder="Buscar"
                        showIcon
                        type="string"
                        value={searchValue}
                      />
                    )}
                  </Col>
                  <Col
                    className={`d-flex ${
                      hasLines ? 'justify-content-around' : 'justify-content-end'
                    }`}
                    sm={7}
                  >
                    {hasLines && (
                      <Button
                        colorIcon="primary"
                        fontSizeLabel="15px"
                        icon={AiOutlineDownload}
                        iconRight
                        label="Descargar parte"
                        onClick={() => {
                          download();
                        }}
                        sizeIcon="17px"
                        type="button"
                        variant="light-gray"
                        widthButton="200px"
                      />
                    )}

                    <Button
                      className="button_template"
                      colorIcon="primary"
                      disabled={!applyTemplate}
                      fontSizeLabel="15px"
                      icon={BiDetail}
                      iconRight
                      label="Plantillas"
                      onClick={() =>
                        activeTemplateWorkReportModal({
                          onAccept: fetchDataWithDefaultParams,
                          monthly: false,
                          applied: false,
                          // Does the request have loaded lines?
                          contractNumber,
                          disabledEditStateCancelOrFinish,
                          endingDate,
                          initialDate
                        })
                      }
                      sizeIcon="18px"
                      type="button"
                      variant="light-gray"
                      widthButton="180px"
                    />

                    <Button
                      colorIcon="primary"
                      disabled={disabledByRol || disabledEditStateCancelOrFinish}
                      fontSizeLabel="15px"
                      icon={BsPlusCircle}
                      iconRight
                      label="Agregar líneas al parte"
                      onClick={() => {
                        setShowModalAddLine(true);
                        fetchPositions();
                      }}
                      sizeIcon="17px"
                      type="button"
                      variant="light-gray"
                      widthButton="200px"
                    />
                  </Col>
                </Row>
              </Col>
            )}
          </div>
        </Col>
      </Row>
      <hr className="mt-0" />
    </>
  );
};

WorkReportHeader.propTypes = {
  contractNumber: PropTypes.number.isRequired,
  disabledByRol: PropTypes.bool.isRequired,
  hasWorkReport: PropTypes.bool.isRequired,
  endingDate: PropTypes.string,
  setInitialDate: PropTypes.func.isRequired,
  setEndingDate: PropTypes.func.isRequired,
  editDate: PropTypes.bool,
  setEditDate: PropTypes.func.isRequired,
  backupDates: PropTypes.objectOf(PropTypes.string),
  setBackupDates: PropTypes.func.isRequired,
  numberDays: PropTypes.number,
  applyTemplate: PropTypes.bool.isRequired,
  setNumberDays: PropTypes.func.isRequired,
  createNewWorkReport: PropTypes.bool.isRequired,
  setShowModalAddLine: PropTypes.func.isRequired,
  setPositions: PropTypes.func.isRequired,
  setCreateNewWorkReport: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  setShowModalOutOfRange: PropTypes.func.isRequired,
  hasLines: PropTypes.bool.isRequired,
  setHasLines: PropTypes.func.isRequired,
  isASDS: PropTypes.bool.isRequired,
  initialDate: PropTypes.string,
  uploadTemplate: PropTypes.func.isRequired,
  disabledEditStateCancelOrFinish: PropTypes.bool.isRequired
};

WorkReportHeader.defaultProps = {
  editDate: null,
  backupDates: null,
  numberDays: null,
  endingDate: '',
  initialDate: ''
};

export default WorkReportHeader;
